import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, BookOpen, FileText, Brain, Trophy, Clock, Target, Info, Zap } from 'lucide-react';

const ElevenPlusPatterns = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const examPatterns = [
    {
      id: 'sutton-set',
      name: 'Sutton SET',
      fullName: 'Sutton Selective Eligibility Test',
      description: 'Competitive test for Sutton grammar schools',
      gradient: 'from-purple-500 to-indigo-500',
      format: 'Computer-based adaptive test',
      duration: '90 minutes total',
      subjects: [
        {
          id: 'maths-sutton',
          name: 'Mathematics',
          icon: '📐',
          color: 'bg-blue-100 border-blue-300',
          duration: '45 minutes',
          questions: '25-30 questions',
          description: 'Adaptive maths test covering arithmetic, algebra, and problem-solving',
          topics: ['Arithmetic', 'Algebra', 'Geometry', 'Fractions & Decimals', 'Percentages', 'Problem Solving'],
          mockPapers: 12,
          format: 'Multiple choice, adaptive difficulty'
        },
        {
          id: 'english-sutton',
          name: 'English',
          icon: '✍️',
          color: 'bg-green-100 border-green-300',
          duration: '45 minutes',
          questions: '25-30 questions',
          description: 'Reading comprehension and language understanding',
          topics: ['Reading Comprehension', 'Vocabulary', 'Grammar', 'Literary Techniques'],
          mockPapers: 12,
          format: 'Multiple choice, adaptive difficulty'
        }
      ]
    },
    {
      id: 'cem',
      name: 'CEM',
      fullName: 'Durham University CEM 11+',
      description: 'Used by many grammar schools including Kent, Bucks, and Warwickshire',
      gradient: 'from-blue-500 to-cyan-500',
      format: 'Paper-based, multiple sections',
      duration: '45-60 minutes per paper',
      subjects: [
        {
          id: 'verbal-reasoning-cem',
          name: 'Verbal Reasoning',
          icon: '💭',
          color: 'bg-purple-100 border-purple-300',
          duration: '45 minutes',
          questions: 'Multiple sections',
          description: 'Mixed verbal reasoning with comprehension',
          topics: ['Comprehension', 'Vocabulary', 'Spelling', 'Grammar', 'Word Problems'],
          mockPapers: 10,
          format: 'Mixed format, shuffled question types'
        },
        {
          id: 'numerical-reasoning-cem',
          name: 'Numerical Reasoning',
          icon: '🔢',
          color: 'bg-blue-100 border-blue-300',
          duration: '45 minutes',
          questions: 'Multiple sections',
          description: 'Numerical problem-solving and reasoning',
          topics: ['Number Operations', 'Problem Solving', 'Data Interpretation', 'Patterns', 'Mental Math'],
          mockPapers: 10,
          format: 'Mixed format, shuffled question types'
        },
        {
          id: 'non-verbal-reasoning-cem',
          name: 'Non-Verbal Reasoning',
          icon: '🔷',
          color: 'bg-orange-100 border-orange-300',
          duration: '20-30 minutes',
          questions: 'Multiple sections',
          description: 'Visual and spatial reasoning patterns',
          topics: ['Shape Patterns', 'Sequences', 'Analogies', 'Matrix Problems'],
          mockPapers: 8,
          format: 'Mixed with verbal sections'
        }
      ]
    },
    {
      id: 'gl-assessment',
      name: 'GL Assessment',
      fullName: 'GL Assessment 11+',
      description: 'Traditional format used by many independent and grammar schools',
      gradient: 'from-green-500 to-emerald-500',
      format: 'Standard paper-based test',
      duration: '45-50 minutes per paper',
      subjects: [
        {
          id: 'english-gl',
          name: 'English',
          icon: '📚',
          color: 'bg-green-100 border-green-300',
          duration: '50 minutes',
          questions: '50-80 questions',
          description: 'Comprehensive English language test',
          topics: ['Comprehension', 'Grammar', 'Punctuation', 'Vocabulary', 'Spelling', 'Sentence Completion'],
          mockPapers: 15,
          format: 'Multiple choice and cloze passages'
        },
        {
          id: 'maths-gl',
          name: 'Mathematics',
          icon: '📐',
          color: 'bg-blue-100 border-blue-300',
          duration: '50 minutes',
          questions: '50-80 questions',
          description: 'Standard mathematical reasoning',
          topics: ['Arithmetic', 'Algebra', 'Geometry', 'Fractions', 'Decimals', 'Percentages', 'Data Handling'],
          mockPapers: 15,
          format: 'Multiple choice'
        },
        {
          id: 'verbal-reasoning-gl',
          name: 'Verbal Reasoning',
          icon: '💭',
          color: 'bg-purple-100 border-purple-300',
          duration: '50 minutes',
          questions: '75-85 questions',
          description: 'Traditional verbal reasoning questions',
          topics: ['Word Meanings', 'Letter Patterns', 'Word Relationships', 'Code Breaking', 'Logic'],
          mockPapers: 12,
          format: '21 different question types'
        },
        {
          id: 'non-verbal-reasoning-gl',
          name: 'Non-Verbal Reasoning',
          icon: '🔷',
          color: 'bg-orange-100 border-orange-300',
          duration: '50 minutes',
          questions: '75-85 questions',
          description: 'Visual and spatial problem solving',
          topics: ['Shape Patterns', 'Sequences', 'Analogies', 'Rotations', 'Reflections', 'Codes'],
          mockPapers: 12,
          format: '16 different question types'
        }
      ]
    },
    {
      id: 'iseb',
      name: 'ISEB',
      fullName: 'ISEB Common Pre-Test',
      description: 'Used by many independent schools for Year 6/7 entry',
      gradient: 'from-orange-500 to-red-500',
      format: 'Computer-based adaptive',
      duration: '2.5 hours total (multiple sessions)',
      subjects: [
        {
          id: 'english-iseb',
          name: 'English',
          icon: '📚',
          color: 'bg-green-100 border-green-300',
          duration: '25 minutes',
          questions: 'Adaptive',
          description: 'Adaptive English comprehension and language',
          topics: ['Reading Comprehension', 'Grammar', 'Vocabulary', 'Spelling'],
          mockPapers: 10,
          format: 'Multiple choice, adaptive'
        },
        {
          id: 'maths-iseb',
          name: 'Mathematics',
          icon: '📐',
          color: 'bg-blue-100 border-blue-300',
          duration: '50 minutes',
          questions: 'Adaptive',
          description: 'Adaptive mathematical reasoning',
          topics: ['Number', 'Algebra', 'Geometry', 'Statistics', 'Problem Solving'],
          mockPapers: 10,
          format: 'Multiple choice, adaptive'
        },
        {
          id: 'verbal-reasoning-iseb',
          name: 'Verbal Reasoning',
          icon: '💭',
          color: 'bg-purple-100 border-purple-300',
          duration: '36 minutes',
          questions: 'Adaptive',
          description: 'Verbal logic and reasoning',
          topics: ['Word Patterns', 'Analogies', 'Logic', 'Sequences'],
          mockPapers: 8,
          format: 'Multiple choice, adaptive'
        },
        {
          id: 'non-verbal-reasoning-iseb',
          name: 'Non-Verbal Reasoning',
          icon: '🔷',
          color: 'bg-orange-100 border-orange-300',
          duration: '32 minutes',
          questions: 'Adaptive',
          description: 'Spatial and visual reasoning',
          topics: ['Shape Patterns', 'Sequences', 'Analogies', 'Matrix Problems'],
          mockPapers: 8,
          format: 'Multiple choice, adaptive'
        }
      ]
    }
  ];

  const currentPattern = examPatterns.find(p => p.id === selectedPattern);
  const currentSubject = currentPattern?.subjects.find(s => s.id === selectedSubject);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-6 px-4 md:px-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
          </Button>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>11+ Exam Patterns</h1>
          <p className="text-white/90">Choose your exam board and subject to start practicing</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
        {/* Exam Pattern Cards */}
        {!selectedPattern ? (
          <div className="grid md:grid-cols-2 gap-6">
            {examPatterns.map((pattern) => (
              <Card
                key={pattern.id}
                className="shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border-2 hover:border-white"
                onClick={() => setSelectedPattern(pattern.id)}
                data-testid={`pattern-${pattern.id}`}
              >
                <CardHeader className={`bg-gradient-to-r ${pattern.gradient} text-white rounded-t-lg`}>
                  <CardTitle className="text-2xl font-bold">{pattern.name}</CardTitle>
                  <CardDescription className="text-white/90 text-sm">{pattern.fullName}</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <p className="text-gray-700">{pattern.description}</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4 text-purple-600" />
                      <span className="font-medium">Format:</span> {pattern.format}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-purple-600" />
                      <span className="font-medium">Duration:</span> {pattern.duration}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Target className="h-4 w-4 text-purple-600" />
                      <span className="font-medium">Subjects:</span> {pattern.subjects.length} papers
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {pattern.subjects.map((subject) => (
                      <Badge key={subject.id} variant="secondary">
                        {subject.name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left Sidebar - Subjects */}
            <div className="w-full md:w-64 space-y-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedPattern(null);
                  setSelectedSubject(null);
                }}
                className="w-full text-white hover:bg-white/20 mb-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Patterns
              </Button>
              <h2 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                {currentPattern.name} Subjects
              </h2>
              {currentPattern.subjects.map((subject) => (
                <Card
                  key={subject.id}
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    selectedSubject === subject.id ? 'ring-2 ring-white shadow-xl' : ''
                  }`}
                  onClick={() => setSelectedSubject(subject.id)}
                  data-testid={`subject-${subject.id}`}
                >
                  <CardContent className={`p-4 ${subject.color} border-2`}>
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{subject.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-800 truncate">{subject.name}</p>
                        <p className="text-xs text-gray-600">{subject.mockPapers} papers</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Right Content - Subject Details */}
            <div className="flex-1">
              {currentSubject ? (
                <Card className="shadow-2xl animate-fade-in" data-testid="subject-details">
                  <CardHeader className={`bg-gradient-to-r ${currentPattern.gradient} text-white rounded-t-lg`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge className="bg-white text-purple-700 font-semibold px-3 py-1 mb-2">
                          {currentPattern.name}
                        </Badge>
                        <CardTitle className="text-3xl font-bold">
                          <span className="text-4xl mr-3">{currentSubject.icon}</span>
                          {currentSubject.name}
                        </CardTitle>
                        <CardDescription className="text-white/90 text-base mt-2">
                          {currentSubject.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4 text-center">
                          <FileText className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-blue-700">{currentSubject.mockPapers}</p>
                          <p className="text-xs text-gray-600">Mock Papers</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="p-4 text-center">
                          <Clock className="h-8 w-8 text-green-600 mx-auto mb-2" />
                          <p className="text-sm font-bold text-green-700">{currentSubject.duration}</p>
                          <p className="text-xs text-gray-600">Duration</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-4 text-center">
                          <Target className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                          <p className="text-sm font-bold text-purple-700">{currentSubject.questions}</p>
                          <p className="text-xs text-gray-600">Questions</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-orange-50 border-orange-200">
                        <CardContent className="p-4 text-center">
                          <Info className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                          <p className="text-xs font-bold text-orange-700">{currentSubject.format}</p>
                          <p className="text-xs text-gray-600">Format</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Topics */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">Topics Covered</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {currentSubject.topics.map((topic, idx) => (
                          <Card key={idx} className="border-2 hover:border-purple-300 transition-colors">
                            <CardContent className="p-3 flex items-center gap-2">
                              <div className="bg-purple-100 text-purple-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                                {idx + 1}
                              </div>
                              <span className="text-sm font-medium">{topic}</span>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3 pt-4">
                      <div className="flex gap-4">
                        <Button 
                          className="flex-1 h-12 text-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                          onClick={() => navigate(`/papers/11plus/${currentSubject.id}`)}
                          data-testid="start-mock-exam"
                        >
                          <Trophy className="mr-2 h-5 w-5" />
                          Start Mock Exam
                        </Button>
                        <Button 
                          variant="outline" 
                          className="flex-1 h-12 text-lg border-2"
                          onClick={() => navigate(`/papers/11plus/${currentSubject.id}`)}
                          data-testid="practice-mode"
                        >
                          <Brain className="mr-2 h-5 w-5" />
                          Practice Mode
                        </Button>
                      </div>
                      
                      {/* Challenger Button */}
                      <Button 
                        onClick={() => navigate(`/challenger/11plus/${currentSubject.id}`)}
                        className="w-full h-14 text-lg bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-600 hover:via-orange-600 hover:to-red-600 text-white font-bold shadow-lg"
                        data-testid="challenger-button"
                      >
                        <Zap className="mr-2 h-6 w-6" />
                        🔥 CHALLENGER MODE
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="shadow-xl">
                  <CardContent className="p-12 text-center">
                    <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">Select a Subject</h3>
                    <p className="text-gray-500">Choose a subject from the left to view mock papers</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElevenPlusPatterns;
